import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import SideMenu from "../component/SideMenu";
import Topbar from "../component/Topbar";
import ProjectDetails from "../component/ProjectDetails";
import { baseurl } from '../baseurl';
import axios from 'axios';

import L from 'leaflet';
import 'leaflet-draw';
import * as turf from '@turf/turf';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import UTIF from 'utif';

// NOTE: requires: npm install leaflet leaflet-draw

const NEARMAP_API_KEY = 'NWI2ZWIzNTYtODdmMS00YmIyLTk5ZGItZDkwNTVjMDM2MzFi';
const MAPBOX_TOKEN = 'pk.eyJ1IjoibWVhc3JvYWRtaW5tYXBib3giLCJhIjoiY21wYzQybXVrMDN5NDJ1c2JkZGhmYWFsNiJ9.aI89Wt7FHAYw3FUonCbKkw';
const NEARMAP_CONTENT_TYPE = 'Vert';

const BUFFER_DISTANCE_METERS = 5;

if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason?.name === 'AbortError' || event.reason?.message?.includes('aborted')) {
            event.preventDefault();
        }
    });
    const originalConsoleError = console.error;
    console.error = (...args) => {
        if (args[0]?.toString?.().includes('AbortError')) return;
        originalConsoleError(...args);
    };
}

let mapInstance = null;

const lon2tileX = (lon, z) => (lon + 180) / 360 * Math.pow(2, z);
const lat2tileY = (lat, z) => {
    const rad = lat * Math.PI / 180;
    return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * Math.pow(2, z);
};

const nearmapTileUrl = (z, x, y, surveyId) =>
    surveyId
        ? `https://api.nearmap.com/tiles/v3/surveys/${surveyId}/${NEARMAP_CONTENT_TYPE}/${z}/${x}/${y}.jpg?apikey=${NEARMAP_API_KEY}`
        : `https://api.nearmap.com/tiles/v3/${NEARMAP_CONTENT_TYPE}/${z}/${x}/${y}.jpg?apikey=${NEARMAP_API_KEY}`;

// =====================================================================
// AI PARCEL DETECTION — PLUGGABLE STUB
// =====================================================================
// Real Nearmap AI Feature API (rooftops/pavement polygons) needs its own
// endpoint + API key that hasn't been provided. Wire your real call here.
// Input: lat/lng + surveyId (optional specific capture date)
// Output: a GeoJSON Feature<Polygon> in [lng, lat] winding order, or null
// if no confident detection was found (caller falls back to a placeholder).
// =====================================================================
// =====================================================================
// AI PARCEL DETECTION — real Nearmap AI Feature API (features.json)
// Endpoint confirmed at developer.nearmap.com/reference/features
// Uses the SAME NEARMAP_API_KEY as your Tile API — but this only works
// if your account's plan actually includes an AI Pack (e.g. "building").
// If it doesn't, this call will 401/403 — see the catch block below.
// =====================================================================
async function detectParcelWithAI(lat, lng, surveyId) {
    try {
        // Nearmap AI needs a Query AOI polygon, not a bare point — build a
        // small starter box (~30m) around the searched location to query into
        const starterAOI = turf.circle(turf.point([lng, lat]), 0.03, {
            units: 'kilometers',
            steps: 8,
        });

        // Flatten ring coords into "lon1,lat1,lon2,lat2,..." as the docs specify
        const ring = starterAOI.geometry.coordinates[0];
        const polygonParam = ring.map(([plng, plat]) => `${plng},${plat}`).join(',');

        const params = {
            polygon: polygonParam,
            apikey: NEARMAP_API_KEY,
            packs: 'building',
        };
        if (surveyId) params.surveyResourceId = surveyId;

        const res = await axios.get(
            'https://api.nearmap.com/ai/features/v4/features.json',
            { params }
        );

        // Response schema isn't shown verbatim in Nearmap's docs, so check
        // the plausible shapes defensively instead of assuming one blindly
        const featureList =
            res.data?.features ||           // GeoJSON FeatureCollection style
            res.data?.featureCollection?.features ||
            res.data?.results ||             // possible custom wrapper
            [];

        if (!Array.isArray(featureList) || featureList.length === 0) {
            return null; // no building features returned — caller uses fallback shape
        }

        // Pick the feature whose centroid is closest to the searched point
        const point = turf.point([lng, lat]);
        let closest = null;
        let closestDist = Infinity;

        for (const f of featureList) {
            const geom = f.geometry || f.geom; // defensive: field name uncertain
            if (!geom || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon')) continue;

            let feature;
            try {
                feature = { type: 'Feature', properties: {}, geometry: geom };
            } catch {
                continue;
            }

            let centroid;
            try {
                centroid = turf.centroid(feature);
            } catch {
                continue;
            }

            const dist = turf.distance(point, centroid, { units: 'meters' });
            if (dist < closestDist) {
                closestDist = dist;
                closest = geom;
            }
        }

        return closest; // GeoJSON Polygon/MultiPolygon, or null if nothing matched
    } catch (err) {
        // 401/403 here almost always means your account doesn't have an
        // AI Pack (e.g. "building") enabled on this key — check your
        // Nearmap dashboard under Account > Subscriptions/API access
        console.error('Nearmap AI Feature API call failed:', err?.response?.status, err?.response?.data || err.message);
        return null;
    }
}

// Fallback boundary used only when detectParcelWithAI() returns null
function fallbackParcelShape(lat, lng) {
    const point = turf.point([lng, lat]);
    return turf.circle(point, 0.05, { units: 'kilometers', steps: 4 });
}

// =====================================================================
// NearmapClipLeafletLayer
// =====================================================================
// =====================================================================
// Replace the entire block from "const NearmapClipLeafletLayer = L.Layer.extend({"
// down through its closing "});" with everything below.
// =====================================================================
// =====================================================================
// Three quality tiers instead of two — a modest "sharper" tier sits
// between the fast default and the expensive full high-quality one.
// This avoids the big lag-causing jump (zoom 19→21 + SCALE 1→2) while
// still giving a noticeably crisper image than the default.
// =====================================================================
const DEFAULT_QUALITY_ZOOM = 19;   // fast first load
const SHARPER_QUALITY_ZOOM = 20;   // modest bump — ~4x pixels, not 15-20x
const HIGH_QUALITY_ZOOM = 21;      // only if you still want the max tier available
const ZOOM_UPGRADE_THRESHOLD = 1.4;  // map zoom must exceed composite zoom by this much to trigger an upgrade

const NEARMAP_TILE_SIZE = 256;

// In-memory cache for this browser session — always checked first (fast, no size limit)
const nearmapImageCache = new Map();

function getNearmapCacheKey(bufferedFeature, surveyId, zoom) {
    const bbox = turf.bbox(bufferedFeature).map((n) => n.toFixed(5)).join(',');
    return `nearmap_img_${bbox}_${surveyId || 'latest'}_z${zoom}`;
}

function readNearmapCache(key) {
    if (nearmapImageCache.has(key)) return nearmapImageCache.get(key);
    try {
        const stored = localStorage.getItem(key);
        if (stored) {
            nearmapImageCache.set(key, stored);
            return stored;
        }
    } catch (e) {
        // localStorage unavailable — in-memory cache still works
    }
    return null;
}

function writeNearmapCache(key, dataUrl) {
    nearmapImageCache.set(key, dataUrl); // instant, no blocking

    const persist = () => {
        try {
            localStorage.setItem(key, dataUrl);
        } catch (e) {
            // Quota exceeded — skip persistence, session cache still holds it
        }
    };

    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(persist, { timeout: 2000 });
    } else {
        setTimeout(persist, 0);
    }
}



// function writeNearmapCache(key, dataUrl) {
//     nearmapImageCache.set(key, dataUrl);
//     try {
//         localStorage.setItem(key, dataUrl);
//     } catch (e) {
//         // Quota exceeded (~5-10MB/origin) — skip persistence, session cache still holds it
//     }
// }

function loadImage(url) {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

// =====================================================================
// Composite — batched + yielded so the main thread stays responsive.
// SCALE dropped from 2 -> 1 by default at the "better quality" tier for
// speed; the high-quality tier (triggered by deep zoom) still uses 2.
// =====================================================================
// =====================================================================
// Compositor worker — does tile fetch + decode + draw entirely off the
// main thread using OffscreenCanvas. This is what actually removes the
// lag on the high-quality upgrade (batching alone still blocks the UI
// thread during each drawImage/decode step).
// =====================================================================
const compositorWorkerSrc = `
self.onmessage = async function (e) {
    const { id, tiles, canvasW, canvasH, tileSize, scale } = e.data;
    try {
        const canvas = new OffscreenCanvas(canvasW, canvasH);
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        const CONCURRENCY = 8;
        for (let i = 0; i < tiles.length; i += CONCURRENCY) {
            const batch = tiles.slice(i, i + CONCURRENCY);
            const loaded = await Promise.all(
                batch.map(async (t) => {
                    try {
                        const res = await fetch(t.url, { signal: e.data.signal });
                        if (!res.ok) return null;
                        const blob = await res.blob();
                        const bitmap = await createImageBitmap(blob);
                        return { ...t, bitmap };
                    } catch {
                        return null;
                    }
                })
            );
            for (const tile of loaded) {
                if (!tile) continue;
                ctx.drawImage(tile.bitmap, tile.dx, tile.dy, tileSize * scale, tileSize * scale);
                tile.bitmap.close();
            }
        }

        const blob = await canvas.convertToBlob({ type: 'image/png' });
        const buf = await blob.arrayBuffer();
        self.postMessage({ id, buf }, [buf]);
    } catch (err) {
        self.postMessage({ id, error: err.message });
    }
};
`;

let compositorWorker = null;
function getCompositorWorker() {
    if (compositorWorker) return compositorWorker;
    const blob = new Blob([compositorWorkerSrc], { type: 'application/javascript' });
    compositorWorker = new Worker(URL.createObjectURL(blob));
    return compositorWorker;
}

let compositeRequestCounter = 0;
function compositeInWorker(tiles, canvasW, canvasH, tileSize, scale) {
    return new Promise((resolve) => {
        const worker = getCompositorWorker();
        const id = ++compositeRequestCounter;

        const handleMessage = (e) => {
            if (e.data.id !== id) return;
            worker.removeEventListener('message', handleMessage);
            if (e.data.error) {
                resolve(null);
                return;
            }
            const blob = new Blob([e.data.buf], { type: 'image/png' });
            resolve(blob);
        };

        worker.addEventListener('message', handleMessage);
        worker.postMessage({ id, tiles, canvasW, canvasH, tileSize, scale });
    });
}

// =====================================================================
// Composite — SCALE stays at 1 for both default AND sharper tiers.
// Only bump SCALE to 2 at the true HIGH_QUALITY_ZOOM tier (or drop that
// tier entirely if SHARPER_QUALITY_ZOOM is now "good enough").
// =====================================================================
async function compositeNearmapImage(bufferedFeature, surveyId, zoom) {
    const bbox = turf.bbox(bufferedFeature);
    const z = Math.min(21, Math.max(1, zoom));
    const SCALE = z >= HIGH_QUALITY_ZOOM ? 2 : 1; // sharper tier (20) still uses SCALE 1

    const worldMinX = lon2tileX(bbox[0], z) * NEARMAP_TILE_SIZE;
    const worldMaxX = lon2tileX(bbox[2], z) * NEARMAP_TILE_SIZE;
    const worldMinY = lat2tileY(bbox[3], z) * NEARMAP_TILE_SIZE;
    const worldMaxY = lat2tileY(bbox[1], z) * NEARMAP_TILE_SIZE;

    const canvasW = Math.ceil((worldMaxX - worldMinX) * SCALE);
    const canvasH = Math.ceil((worldMaxY - worldMinY) * SCALE);
    if (canvasW <= 0 || canvasH <= 0 || canvasW > 8000 || canvasH > 8000) return null;

    const xMin = Math.floor(worldMinX / NEARMAP_TILE_SIZE);
    const xMax = Math.floor(worldMaxX / NEARMAP_TILE_SIZE);
    const yMin = Math.floor(worldMinY / NEARMAP_TILE_SIZE);
    const yMax = Math.floor(worldMaxY / NEARMAP_TILE_SIZE);

    const tiles = [];
    for (let tx = xMin; tx <= xMax; tx++) {
        for (let ty = yMin; ty <= yMax; ty++) {
            tiles.push({
                url: nearmapTileUrl(z, tx, ty, surveyId),
                dx: (tx * NEARMAP_TILE_SIZE - worldMinX) * SCALE,
                dy: (ty * NEARMAP_TILE_SIZE - worldMinY) * SCALE,
            });
        }
    }

    const blob = await compositeInWorker(tiles, canvasW, canvasH, NEARMAP_TILE_SIZE, SCALE);
    if (!blob) return null;

    const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });

    return {
        dataUrl,
        bounds: L.latLngBounds([[bbox[1], bbox[0]], [bbox[3], bbox[2]]]),
    };
}

// =====================================================================
// NearmapClipLeafletLayer — debounce the zoom-triggered upgrade so a
// user scrolling through zoom levels quickly doesn't fire several
// overlapping high-quality composites; only the final zoom level (after
// 350ms of no further zoom change) triggers a fetch.
// =====================================================================
const ZOOM_DEBOUNCE_MS = 350;

const NearmapClipLeafletLayer = L.Layer.extend({
    initialize: function (bufferedFeature, surveyId, onImageReady, onLoadingChange) {
        this._bufferedFeature = bufferedFeature;
        this._surveyId = surveyId || null;
        this._overlay = null;
        this._loadToken = 0;
        this._onImageReady = onImageReady || null;
        this._onLoadingChange = onLoadingChange || null;
        this._compositeZoom = null;
        this._zoomDebounceTimer = null;
        this._prevMaxZoom = null;
        this._onMapZoom = this._onMapZoom.bind(this);
    },
    onAdd: function (map) {
        this._map = map;
        map.on('zoomend', this._onMapZoom);

        // Restrict zoom — user can't zoom in past what our highest quality
        // tier actually supports, so they never hit a soft "nothing sharper
        // available" ceiling with no feedback
        this._prevMaxZoom = map.getMaxZoom();
        map.setMaxZoom(HIGH_QUALITY_ZOOM);

        this._composite(this._getBaseZoom());
        return this;
    },
    onRemove: function (map) {
        map.off('zoomend', this._onMapZoom);
        if (this._zoomDebounceTimer) clearTimeout(this._zoomDebounceTimer);

        // Restore whatever max zoom the base map had before this layer
        // took over (e.g. mapbox's 22)
        if (this._prevMaxZoom != null) {
            map.setMaxZoom(this._prevMaxZoom);
        }

        if (this._overlay) {
            map.removeLayer(this._overlay);
            this._overlay = null;
        }
        if (this._onLoadingChange) this._onLoadingChange(false);
    },
    _getBaseZoom: function () {
        const areaSqM = turf.area(this._bufferedFeature);
        let zoom = DEFAULT_QUALITY_ZOOM;
        if (areaSqM > 200000) zoom = 17;
        else if (areaSqM > 50000) zoom = 18;
        else if (areaSqM > 10000) zoom = DEFAULT_QUALITY_ZOOM;
        return zoom;
    },
    _nextTier: function () {
        if (this._compositeZoom < SHARPER_QUALITY_ZOOM) return SHARPER_QUALITY_ZOOM;
        if (this._compositeZoom < HIGH_QUALITY_ZOOM) return HIGH_QUALITY_ZOOM;
        return null; // already at max tier — nowhere further to go
    },
    _onMapZoom: function () {
        if (!this._map || this._compositeZoom == null) return;

        if (this._zoomDebounceTimer) clearTimeout(this._zoomDebounceTimer);
        this._zoomDebounceTimer = setTimeout(() => {
            const mapZoom = this._map.getZoom();
            const nextZoom = this._nextTier();
            if (nextZoom && mapZoom >= this._compositeZoom + ZOOM_UPGRADE_THRESHOLD) {
                this._composite(nextZoom);
            }
        }, ZOOM_DEBOUNCE_MS);
    },
    _composite: async function (zoom) {
        const map = this._map;
        if (!map) return;
        const token = ++this._loadToken;

        const cacheKey = getNearmapCacheKey(this._bufferedFeature, this._surveyId, zoom);
        const cached = readNearmapCache(cacheKey);

        let dataUrl, bounds;
        if (cached) {
            dataUrl = cached;
            const bbox = turf.bbox(this._bufferedFeature);
            bounds = L.latLngBounds([[bbox[1], bbox[0]], [bbox[3], bbox[2]]]);
        } else {
            // Not cached — this is real work, show the loader
            if (this._onLoadingChange) this._onLoadingChange(true);

            const result = await compositeNearmapImage(this._bufferedFeature, this._surveyId, zoom);

            if (token !== this._loadToken || !this._map) {
                // Superseded while loading — clear loader only if nothing
                // newer is still in flight
                if (this._onLoadingChange && token === this._loadToken) this._onLoadingChange(false);
                return;
            }

            if (!result) {
                if (this._onLoadingChange) this._onLoadingChange(false);
                return;
            }
            dataUrl = result.dataUrl;
            bounds = result.bounds;
            writeNearmapCache(cacheKey, dataUrl);
        }

        if (token !== this._loadToken || !this._map) return;

        if (this._overlay) this._map.removeLayer(this._overlay);
        this._overlay = L.imageOverlay(dataUrl, bounds, {
            interactive: false,
            opacity: 1,
        }).addTo(this._map);
        this._compositeZoom = zoom;

        if (this._onLoadingChange) this._onLoadingChange(false);
        if (this._onImageReady) {
            this._onImageReady({ dataUrl, surveyId: this._surveyId, bounds, zoom });
        }
    },
    setSurveyId: function (surveyId) {
        if (this._surveyId === surveyId) return;
        this._surveyId = surveyId;
        this._compositeZoom = null;
        this._composite(this._getBaseZoom());
    },
});








// =====================================================================
// LeafletDrawControls
// =====================================================================
const LeafletDrawControls = ({ onAreaChange, setGeometry, initialGeometry, map, onParcelsChange, autoBoundary, onBoundaryEdited, editRequest, clearRequest }) => {
    const drawnItemsRef = useRef(null);
    const bufferLayerRef = useRef(null);
    const selectedLayerRef = useRef(null);
    const isLoadedRef = useRef(false);
    const appliedAutoTokenRef = useRef(null);

    const updateMapDataRef = useRef(null);
    const applyBufferAndFitRef = useRef(null);
    const activeToolRef = useRef(null);
    const disableActiveToolRef = useRef(null);

    useEffect(() => {
    if (!map) return;

    // =====================================================================
    // GUARD — mapInstance is a module-level singleton that survives
    // component remounts (route revisits, Fast Refresh, etc.). If this
    // effect ran on this same map before, stale CREATED listeners and
    // leftover FeatureGroups/toolbar can still be attached — causing a
    // freshly drawn shape to be added twice (duplicate parcel bug).
    // Clean all of that up BEFORE creating anything new.
    // =====================================================================
    map.off(L.Draw.Event.CREATED);

    if (map._nearmapDrawnItems) {
        map.removeLayer(map._nearmapDrawnItems);
        map._nearmapDrawnItems = null;
    }
    if (map._nearmapBufferLayer) {
        map.removeLayer(map._nearmapBufferLayer);
        map._nearmapBufferLayer = null;
    }
    const staleToolbar = map.getContainer().querySelector('.custom-draw-toolbar');
    if (staleToolbar) staleToolbar.remove();

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;
    map._nearmapDrawnItems = drawnItems; // tag on map so a future remount can find/clean it

    const bufferLayer = new L.FeatureGroup();
    map.addLayer(bufferLayer);
    bufferLayerRef.current = bufferLayer;
    map._nearmapBufferLayer = bufferLayer; // tag on map for the same reason

    const toolbarContainer = document.createElement('div');
    toolbarContainer.className = 'custom-draw-toolbar';
    toolbarContainer.style.cssText = `
        position: absolute; top: 10px; left: 5px;
        display: flex; flex-direction: column; gap: 5px; z-index: 1000;
    `;

    if (!document.getElementById('draw-toolbar-styles')) {
        const style = document.createElement('style');
        style.id = 'draw-toolbar-styles';
        style.textContent = `
            .draw-tool-btn { width: 34px; height: 34px; background: #ffffff; border: 1.5px solid #e2e2e2; border-radius: 8px; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 6px rgba(0,0,0,0.10); transition: all 0.15s ease; padding: 0; position: relative; }
            .draw-tool-btn svg { width: 20px; height: 20px; stroke: #666; fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; transition: stroke 0.15s ease; }
            .draw-tool-btn:hover { background: #6a11cb; border-color: #6a11cb; box-shadow: 0 4px 12px rgba(106,17,203,0.30); transform: translateY(-1px); }
            .draw-tool-btn:hover svg { stroke: #ffffff; }
            .draw-tool-btn:active { transform: translateY(0); box-shadow: 0 2px 5px rgba(106,17,203,0.20); }
            .draw-tool-btn.active-tool { background: #6a11cb; border-color: #5a0db5; box-shadow: 0 4px 12px rgba(106,17,203,0.30); }
            .draw-tool-btn.active-tool svg { stroke: #ffffff; }
            .draw-tool-btn.active-tool svg .point-inner { fill: #6a11cb; stroke: #ffffff; }
            .draw-tool-btn:hover svg .point-inner { fill: #6a11cb; stroke: #ffffff; }
            .draw-tool-btn svg .point-dot { fill: #666; stroke: none; transition: fill 0.15s ease; }
            .draw-tool-btn:hover svg .point-dot, .draw-tool-btn.active-tool svg .point-dot { fill: #ffffff; }
            .draw-tool-btn[data-tooltip]::after { content: attr(data-tooltip); position: absolute; left: 42px; top: 50%; transform: translateY(-50%); background: #1a1a2e; color: #fff; font-size: 11px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-weight: 500; padding: 4px 9px; border-radius: 5px; white-space: nowrap; pointer-events: none; z-index: 9999; opacity: 0; transition: opacity 0.15s ease; box-shadow: 0 2px 8px rgba(0,0,0,0.18); }
            .draw-tool-btn[data-tooltip]::before { content: ''; position: absolute; left: 38px; top: 50%; transform: translateY(-50%); border: 4px solid transparent; border-right-color: #1a1a2e; pointer-events: none; z-index: 9999; opacity: 0; transition: opacity 0.15s ease; }
            .draw-tool-btn[data-tooltip]:hover::after, .draw-tool-btn[data-tooltip]:hover::before { opacity: 1; }
            .draw-toolbar-divider { width: 34px; height: 1px; background: #e2e2e2; margin: 1px 0; }
        `;
        document.head.appendChild(style);
    }

    // =====================================================================
    // Second safety net — even if a duplicate listener somehow still
    // fires twice for one draw action, this dedupes by comparing each
    // layer's serialized geometry so identical shapes never appear twice
    // in the parcels list.
    // =====================================================================
    const updateMapData = () => {
        const seenGeometries = new Set();
        const features = [];
        drawnItems.eachLayer((layer) => {
            const feature = layer.toGeoJSON();
            const sig = JSON.stringify(feature.geometry);
            if (seenGeometries.has(sig)) {
                // Exact duplicate geometry — drop the redundant layer from
                // the map too, so it doesn't linger as an invisible overlap
                drawnItems.removeLayer(layer);
                return;
            }
            seenGeometries.add(sig);
            features.push(feature);
        });
        let totalArea = 0;
        features.forEach((f) => {
            if (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon') totalArea += turf.area(f);
        });
        onAreaChange(totalArea);
        setGeometry(features.length > 0 ? features : null);
        onParcelsChange(features);
    };
    updateMapDataRef.current = updateMapData;

    const applyBufferAndFit = (layer) => {
        const geojson = layer.toGeoJSON();
        if (geojson.geometry.type !== 'Polygon' && geojson.geometry.type !== 'MultiPolygon') return;
        let buffered;
        try {
            buffered = turf.buffer(geojson, BUFFER_DISTANCE_METERS, { units: 'meters' });
        } catch (err) {
            console.error('Buffer calculation error:', err);
            return;
        }
        if (!buffered) return;

        bufferLayer.clearLayers();
        const bufferGeoLayer = L.geoJSON(buffered, {
            style: { color: '#00e5ff', weight: 0, dashArray: '6, 6', fill: false, interactive: false },
        });
        bufferGeoLayer.addTo(bufferLayer);
        map.fitBounds(bufferGeoLayer.getBounds(), { padding: [40, 40] });
    };
    applyBufferAndFitRef.current = applyBufferAndFit;

    const clearActiveTools = () => {
        toolbarContainer.querySelectorAll('.draw-tool-btn').forEach((b) => b.classList.remove('active-tool'));
    };

    disableActiveToolRef.current = () => {
        if (activeToolRef.current) {
            activeToolRef.current.disable();
            activeToolRef.current = null;
        }
        clearActiveTools();
    };

    const enableEditingOn = (layer) => {
        disableActiveToolRef.current && disableActiveToolRef.current();
        if (selectedLayerRef.current && selectedLayerRef.current !== layer) {
            selectedLayerRef.current.editing && selectedLayerRef.current.editing.disable();
        }
        selectedLayerRef.current = layer;
        if (layer.editing) layer.editing.enable();
    };

    const attachLayerInteractions = (layer) => {
        layer.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            enableEditingOn(layer);
        });
        layer.on('edit', () => {
            updateMapData();
            applyBufferAndFit(layer);
            if (onBoundaryEdited) onBoundaryEdited();
        });
    };

    const tools = [
        { mode: 'polygon', title: 'Draw Polygon', svg: `<svg viewBox="0 0 24 24"><polygon points="12,2 21,8 18,20 6,20 3,8"/></svg>` },
        { mode: 'rectangle', title: 'Draw Rectangle', svg: `<svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="1.5"/></svg>` },
        { mode: 'circle', title: 'Draw Circle', svg: `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/></svg>` },
        { mode: 'polyline', title: 'Draw Line', svg: `<svg viewBox="0 0 24 24"><line x1="4" y1="20" x2="20" y2="4"/><circle cx="4" cy="20" r="2" class="point-dot"/><circle cx="20" cy="4" r="2" class="point-dot"/></svg>` },
        { mode: 'marker', title: 'Place Point', svg: `<svg viewBox="0 0 24 24"><path d="M12 2C8.69 2 6 4.69 6 8c0 4.5 6 14 6 14s6-9.5 6-14c0-3.31-2.69-6-6-6z"/><circle cx="12" cy="8" r="2.2" class="point-inner"/></svg>` },
    ];

    tools.forEach(({ mode, title, svg }, i) => {
        if (i === 3) {
            const divider = document.createElement('div');
            divider.className = 'draw-toolbar-divider';
            toolbarContainer.appendChild(divider);
        }

        const btn = document.createElement('button');
        btn.className = 'draw-tool-btn';
        btn.setAttribute('data-tooltip', title);
        btn.setAttribute('data-mode', mode);
        btn.innerHTML = svg;

        btn.addEventListener('click', () => {
            if (activeToolRef.current) {
                activeToolRef.current.disable();
                activeToolRef.current = null;
            }

            if (mode === 'trash') {
                if (selectedLayerRef.current) {
                    drawnItems.removeLayer(selectedLayerRef.current);
                    selectedLayerRef.current = null;
                    bufferLayer.clearLayers();
                    updateMapData();
                }
                clearActiveTools();
                return;
            }

            clearActiveTools();
            btn.classList.add('active-tool');

            const drawOptions = {
                shapeOptions: { color: '#6a11cb', weight: 2.5, fillColor: '#6a11cb', fillOpacity: 0 },
            };

            if (mode === 'polygon') activeToolRef.current = new L.Draw.Polygon(map, drawOptions);
            else if (mode === 'rectangle') activeToolRef.current = new L.Draw.Rectangle(map, drawOptions);
            else if (mode === 'circle') activeToolRef.current = new L.Draw.Circle(map, drawOptions);
            else if (mode === 'polyline') activeToolRef.current = new L.Draw.Polyline(map, { shapeOptions: drawOptions.shapeOptions });
            else if (mode === 'marker') activeToolRef.current = new L.Draw.Marker(map);

            activeToolRef.current.enable();
        });

        toolbarContainer.appendChild(btn);
    });

    // Guard against double-firing at the source too — L.Draw can, in rare
    // cases (rapid clicks finishing a shape, or a stray duplicate handler),
    // emit CREATED twice for one action. This flag collapses same-tick
    // duplicate calls into one.
    let handlingCreate = false;
    map.on(L.Draw.Event.CREATED, (e) => {
        if (handlingCreate) return;
        handlingCreate = true;

        activeToolRef.current = null;
        const layer = e.layer;
        drawnItems.addLayer(layer);
        attachLayerInteractions(layer);
        clearActiveTools();
        updateMapData();
        if (e.layerType === 'polygon' || e.layerType === 'rectangle' || e.layerType === 'circle') {
            applyBufferAndFit(layer);
        }

        setTimeout(() => { handlingCreate = false; }, 0);
    });

    map.getContainer().appendChild(toolbarContainer);

    return () => {
        map.off(L.Draw.Event.CREATED);
        map.removeLayer(drawnItems);
        map.removeLayer(bufferLayer);
        map._nearmapDrawnItems = null;
        map._nearmapBufferLayer = null;
        const toolbar = map.getContainer().querySelector('.custom-draw-toolbar');
        if (toolbar) toolbar.remove();
    };
}, []);

    useEffect(() => {
        if (!map || !drawnItemsRef.current || !initialGeometry || isLoadedRef.current) return;
        const drawnItems = drawnItemsRef.current;
        let features = [];
        if (initialGeometry.features) features = initialGeometry.features;
        else if (Array.isArray(initialGeometry)) features = initialGeometry;
        else if (initialGeometry.type === 'Feature') features = [initialGeometry];

        if (features.length > 0) {
            const geoLayer = L.geoJSON(features, {
                style: { color: '#6a11cb', weight: 2.5, fillColor: '#6a11cb', fillOpacity: 0 },
            });

            geoLayer.eachLayer((layer) => {
                drawnItems.addLayer(layer);
                layer.on('click', (e) => {
                    L.DomEvent.stopPropagation(e);
                    disableActiveToolRef.current && disableActiveToolRef.current();
                    if (selectedLayerRef.current && selectedLayerRef.current !== layer) {
                        selectedLayerRef.current.editing && selectedLayerRef.current.editing.disable();
                    }
                    selectedLayerRef.current = layer;
                    if (layer.editing) layer.editing.enable();
                });
                layer.on('edit', () => {
                    updateMapDataRef.current && updateMapDataRef.current();
                    if (applyBufferAndFitRef.current) applyBufferAndFitRef.current(layer);
                    if (onBoundaryEdited) onBoundaryEdited();
                });
                if (applyBufferAndFitRef.current) applyBufferAndFitRef.current(layer);
            });

            if (geoLayer.getBounds().isValid()) map.fitBounds(geoLayer.getBounds(), { padding: [40, 40] });
            isLoadedRef.current = true;

            let totalArea = 0;
            features.forEach((f) => {
                if (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon') totalArea += turf.area(f);
            });
            onAreaChange(totalArea);
            setGeometry(features);
        }
    }, [initialGeometry, map]);

    useEffect(() => {
        if (!map || !drawnItemsRef.current || !autoBoundary || !autoBoundary.feature) return;
        if (appliedAutoTokenRef.current === autoBoundary.token) return;
        appliedAutoTokenRef.current = autoBoundary.token;

        const drawnItems = drawnItemsRef.current;
        drawnItems.clearLayers();

        const geoLayer = L.geoJSON(autoBoundary.feature, {
            style: { color: '#6a11cb', weight: 2.5, fillColor: '#6a11cb', fillOpacity: 0 },
        });

        geoLayer.eachLayer((layer) => {
            drawnItems.addLayer(layer);
            layer.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                disableActiveToolRef.current && disableActiveToolRef.current();
                if (selectedLayerRef.current && selectedLayerRef.current !== layer) {
                    selectedLayerRef.current.editing && selectedLayerRef.current.editing.disable();
                }
                selectedLayerRef.current = layer;
                if (layer.editing) layer.editing.enable();
            });
            layer.on('edit', () => {
                updateMapDataRef.current && updateMapDataRef.current();
                if (applyBufferAndFitRef.current) applyBufferAndFitRef.current(layer);
                if (onBoundaryEdited) onBoundaryEdited();
            });
            if (applyBufferAndFitRef.current) applyBufferAndFitRef.current(layer);
        });

        if (updateMapDataRef.current) updateMapDataRef.current();
    }, [autoBoundary, map]);

    useEffect(() => {
        if (!map || !drawnItemsRef.current || !editRequest) return;
        disableActiveToolRef.current && disableActiveToolRef.current();
        drawnItemsRef.current.eachLayer((layer) => {
            if (layer.editing) layer.editing.enable();
        });
    }, [editRequest, map]);

    useEffect(() => {
        if (!map || !drawnItemsRef.current || !clearRequest) return;
        drawnItemsRef.current.clearLayers();
        if (bufferLayerRef.current) bufferLayerRef.current.clearLayers();
        selectedLayerRef.current = null;
        appliedAutoTokenRef.current = null;
        updateMapDataRef.current && updateMapDataRef.current();
    }, [clearRequest, map]);

    return null;
};

// =====================================================================
// AddressConfirmPanel — new: confirm the searched address before
// running AI parcel detection
// =====================================================================
const AddressConfirmPanel = ({ locationName, onConfirm, onCancel, isDetecting }) => (
    <div style={{
        position: 'absolute', top: '10px', left: '50px', width: '280px',
        background: '#fff', borderRadius: '14px', padding: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 1001, fontFamily: 'sans-serif',
    }}>
        <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>Confirm Location</div>
        <div style={{ fontSize: '13px', color: '#333', marginBottom: '14px', lineHeight: 1.4 }}>
            {locationName}
        </div>
        {isDetecting ? (
            <div style={{ fontSize: '12px', color: '#6a11cb', fontWeight: 600, textAlign: 'center', padding: '8px' }}>
                Detecting parcel boundary…
            </div>
        ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
                <button
                    style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                    onClick={onConfirm}
                >
                    ✓ Confirm Address
                </button>
                <button
                    style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', color: '#333', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                    onClick={onCancel}
                >
                    Cancel
                </button>
            </div>
        )}
    </div>
);

// =====================================================================
// ModifyParcelPanel
// =====================================================================
const ModifyParcelPanel = ({
    parcels, totalAreaAcres, onConfirm, confirmed,
    imagerySource, onImageryChange,
    surveys, selectedSurveyId, onSurveyChange,
    onEditBoundary, onRemoveBoundary,
}) => (
    <div style={{
        position: 'absolute', top: '10px', left: '50px', width: '280px',
        background: '#e9efff', borderRadius: '14px', padding: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 1001, fontFamily: 'sans-serif',
    }}>
        <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '10px' }}>Modify Parcel</div>
        <div style={{ fontSize: '11px', color: '#4b6b4f', marginBottom: '12px' }}>
            Please note that you can add multiple Parcels/ AOI on single order.
        </div>

        {parcels.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', background: '#fff', borderRadius: '8px', padding: '8px 10px', marginBottom: '6px' }}>
                <span style={{ width: '10px', height: '10px', background: '#f2a900', borderRadius: '2px', display: 'inline-block', marginRight: '8px' }} />
                <span style={{ fontSize: '12px', flex: 1 }}>Parcel {i + 1} area:</span>
                <span style={{ fontSize: '12px', fontWeight: 600 }}>{((turf.area(p) / 4046.8564224)).toFixed(2)} acre</span>
            </div>
        ))}

        <div style={{ background: '#dff0e0', borderRadius: '8px', padding: '10px', fontSize: '12px', fontWeight: 600, textAlign: 'center', marginBottom: '10px' }}>
            🌿 Total area {totalAreaAcres.toFixed(2)} acre
        </div>

        {!confirmed ? (
            <>
                <button
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                    onClick={onConfirm}
                    disabled={parcels.length === 0}
                >
                    ✓ Confirm Boundary
                </button>
                <button
                    style={{ width: '100%', padding: '9px', borderRadius: '10px', marginTop: '8px', border: '1px solid #c0392b', background: '#fff', color: '#c0392b', fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}
                    onClick={onRemoveBoundary}
                >
                    ✕ Remove & Draw New Boundary
                </button>
            </>
        ) : (
            <div>
                <button
                    style={{ width: '100%', padding: '9px', borderRadius: '10px', border: '1px solid #2575fc', background: '#fff', color: '#2575fc', fontWeight: 600, fontSize: '12px', cursor: 'pointer', marginBottom: '10px' }}
                    onClick={onEditBoundary}
                >
                    ✎ Edit Boundary
                </button>

                <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>Select Imagery</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: imagerySource === 'nearmap' ? '10px' : 0 }}>
                    <input
                        type="checkbox"
                        id="hd-image-toggle"
                        checked={imagerySource === 'nearmap'}
                        onChange={(e) => onImageryChange(e.target.checked ? 'nearmap' : 'mapbox')}
                        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6a11cb' }}
                    />
                    <label htmlFor="hd-image-toggle" style={{ fontSize: '13px', fontWeight: 600, color: '#333', cursor: 'pointer' }}>
                        HD Image
                    </label>
                </div>

                {imagerySource === 'nearmap' && (
                    <div>
                        <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>Capture Date</div>
                        {surveys && surveys.length > 0 ? (
                            <select
                                value={selectedSurveyId || ''}
                                onChange={(e) => onSurveyChange(e.target.value)}
                                style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '12px' }}
                            >
                                {surveys.map((s) => (
                                    <option key={s.id} value={s.id}>{s.captureDate || s.date || s.id}</option>
                                ))}
                            </select>
                        ) : (
                            <div style={{ fontSize: '11px', color: '#888' }}>No survey dates available — showing latest.</div>
                        )}
                    </div>
                )}
            </div>
        )}
    </div>
);

// =====================================================================
// Home
// =====================================================================
const Home = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const propertyId = searchParams.get('propertyid');
    const action = searchParams.get('action');

    const [mapReady, setMapReady] = useState(false);
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);

    const [position, setPosition] = useState([42.3314, -83.0458]);
    const [locationName, setLocationName] = useState("Detroit, MI");
    const [totalArea, setTotalArea] = useState(0);
    const [geometry, setGeometry] = useState(null);
    const [editData, setEditData] = useState(null);

    const [parcels, setParcels] = useState([]);
    const [boundaryConfirmed, setBoundaryConfirmed] = useState(false);
    const [imagerySource, setImagerySource] = useState('mapbox');
    const nearmapClipLayerRef = useRef(null);
    const mapboxBaseLayerRef = useRef(null);

    const [nearmapSurveys, setNearmapSurveys] = useState([]);
    const [selectedSurveyId, setSelectedSurveyId] = useState(null);
    const [autoBoundary, setAutoBoundary] = useState(null);
    const [editRequestToken, setEditRequestToken] = useState(0);
    const [clearRequestToken, setClearRequestToken] = useState(0);


    const [nearmapImageData, setNearmapImageData] = useState(null);
const [isNearmapLoading, setIsNearmapLoading] = useState(false);


    // NEW — pending address confirmation state (before AI detection runs)
    const [pendingLocation, setPendingLocation] = useState(null); // { lat, lng, name, surveyId, surveys }
    const [isDetecting, setIsDetecting] = useState(false);

    const lastParcelsSignatureRef = useRef(null);

    const [config, setConfig] = useState({
        measurement_unit: 'Sqft',
        default_report_type: 'Landscaping + Snow',
    });

    const userToken = localStorage.getItem('userToken');

    const handleRemoveBoundary = () => {
        setAutoBoundary(null);
        setParcels([]);
        setGeometry(null);
        setTotalArea(0);
        setBoundaryConfirmed(false);
        setImagerySource('mapbox');
        setNearmapSurveys([]);
        setSelectedSurveyId(null);
        setClearRequestToken((t) => t + 1);
    };

    const handleBoundaryEdited = () => {
        setBoundaryConfirmed(false);
        setImagerySource('mapbox');
    };

    const handleEditBoundaryClick = () => {
        setBoundaryConfirmed(false);
        setImagerySource('mapbox');
        setEditRequestToken((t) => t + 1);
    };

    // STEP 1 — Address searched: fly to it, show marker, but WAIT for confirmation
    // before running AI detection (per the requested flow)
    // Home — handleLocationSearch: stop auto-opening the popup, and use a
// custom small pin icon instead of Leaflet's oversized default marker
const handleLocationSearch = (lat, lng, name, surveyId, surveys) => {
    if (!mapRef.current) return;

    setPosition([lat, lng]);
    setLocationName(name || "Custom Location");
    mapRef.current.flyTo([lat, lng], 18);

    if (markerRef.current) mapRef.current.removeLayer(markerRef.current);

    const pinIcon = L.divIcon({
        className: 'search-pin-icon',
        html: `<svg width="26" height="34" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg">
                 <path d="M13 0C5.8 0 0 5.8 0 13c0 9.5 13 21 13 21s13-11.5 13-21C26 5.8 20.2 0 13 0z" fill="#6a11cb"/>
                 <circle cx="13" cy="13" r="5.5" fill="#ffffff"/>
               </svg>`,
        iconSize: [26, 34],
        iconAnchor: [13, 34], // tip of the pin points at the exact coordinate
    });

    markerRef.current = L.marker([lat, lng], { icon: pinIcon }).addTo(mapRef.current);
    // No .bindPopup(...).openPopup() — address tooltip no longer shows

    // Reset any previous parcel state — new search means starting over
    setAutoBoundary(null);
    setParcels([]);
    setGeometry(null);
    setTotalArea(0);
    setBoundaryConfirmed(false);
    setImagerySource('mapbox');
    setNearmapSurveys(surveys || []);
    setSelectedSurveyId(surveyId || null);
    setClearRequestToken((t) => t + 1);

    setPendingLocation({ lat, lng, name, surveyId, surveys });
};

    // STEP 2 — User confirms the address → fetch Nearmap image for that plot
    // → run AI detection → overlay the detected polygon on Mapbox
    // Home — handleConfirmAddress: remove the search pin once the boundary
// is detected, so it doesn't clutter the parcel view (optional — remove
// this block if you'd rather keep the pin visible after confirming)
const handleConfirmAddress = async () => {
    if (!pendingLocation) return;
    const { lat, lng, surveyId, surveys } = pendingLocation;

    setIsDetecting(true);
    try {
        let detectedGeometry = null;

        if (surveys && surveys.length > 0) {
            detectedGeometry = await detectParcelWithAI(lat, lng, surveyId);
        }

        const finalFeature = detectedGeometry
            ? { type: 'Feature', properties: {}, geometry: detectedGeometry }
            : fallbackParcelShape(lat, lng);

        setAutoBoundary({ feature: finalFeature, token: `${lat},${lng},${Date.now()}` });

        // Remove the pin once a boundary is drawn — the polygon itself
        // now marks the location, so the pin becomes redundant clutter
        if (markerRef.current && mapRef.current) {
            mapRef.current.removeLayer(markerRef.current);
            markerRef.current = null;
        }
    } catch (err) {
        console.error('Parcel detection error:', err);
        setAutoBoundary({ feature: fallbackParcelShape(lat, lng), token: `${lat},${lng},${Date.now()}` });
    } finally {
        setIsDetecting(false);
        setPendingLocation(null);
    }
};

    const handleCancelAddress = () => {
        setPendingLocation(null);
        if (markerRef.current && mapRef.current) {
            mapRef.current.removeLayer(markerRef.current);
            markerRef.current = null;
        }
    };

    // Nearmap clip layer effect
  // Home.js — replace the entire imagerySource/nearmap effect's "try" block
useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!boundaryConfirmed || imagerySource !== 'nearmap' || parcels.length === 0) {
    if (nearmapClipLayerRef.current) {
        map.removeLayer(nearmapClipLayerRef.current);
        nearmapClipLayerRef.current = null;
    }
    lastParcelsSignatureRef.current = null;
    setNearmapImageData(null);
    setIsNearmapLoading(false); // ← NEW
    return;
}

    const currentSignature = JSON.stringify(parcels.map((p) => p.geometry));
    const parcelsChanged = currentSignature !== lastParcelsSignatureRef.current;

    if (nearmapClipLayerRef.current && !parcelsChanged) {
        nearmapClipLayerRef.current.setSurveyId(selectedSurveyId);
        return;
    }

    if (nearmapClipLayerRef.current) {
        map.removeLayer(nearmapClipLayerRef.current);
        nearmapClipLayerRef.current = null;
    }

   try {
    const featureCollection = turf.featureCollection(parcels);
    const combinedBbox = turf.bbox(featureCollection);
    const bboxPolygon = turf.bboxPolygon(combinedBbox);

    const buffered = turf.buffer(bboxPolygon, BUFFER_DISTANCE_METERS, { units: 'meters' });
    if (buffered) {
        const clipLayer = new NearmapClipLeafletLayer(
            buffered,
            selectedSurveyId,
            ({ dataUrl, surveyId }) => {
                const surveyMeta = nearmapSurveys.find((s) => s.id === surveyId);
                const captureDate = surveyMeta?.captureDate || surveyMeta?.date || null;

                const payload = {
                    dataUrl,
                    surveyId: surveyId || null,
                    captureDate,
                    fetchedAt: new Date().toISOString(),
                };

                setNearmapImageData(payload);
            },
            (loading) => setIsNearmapLoading(loading) // ← NEW
        );
        clipLayer.addTo(map);
        nearmapClipLayerRef.current = clipLayer;
        lastParcelsSignatureRef.current = currentSignature;
    }
} catch (err) {
    console.error('Nearmap clip layer error:', err);
}
}, [imagerySource, boundaryConfirmed, parcels, selectedSurveyId, nearmapSurveys]);












    useEffect(() => {
        const fetchData = async () => {
            try {
                const prefRes = await axios.get(`${baseurl}getUserPreferences`, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });
                if (prefRes.data.success && prefRes.data.data.measurement_settings) {
                    setConfig(prev => ({ ...prev, ...prefRes.data.data.measurement_settings }));
                }

                if (action === 'edit' && propertyId) {
                    const projectRes = await axios.get(`${baseurl}getProjectByPropertyId/${propertyId}`, {
                        headers: { Authorization: `Bearer ${userToken}` }
                    });

                    if (projectRes.data.success) {
                        const project = projectRes.data.data;
                        setEditData(project);
                        setLocationName(project.project_data.address);
                        setPosition(project.project_data.coords);
                        setGeometry(project.geometry);
                    }
                }
            } catch (err) {
                console.error("Fetch error:", err);
            }
        };
        fetchData();
    }, [userToken, action, propertyId]);

    useEffect(() => {
        if (mapInstance) {
            mapRef.current = mapInstance;
            mapInstance.eachLayer((layer) => {
                if (layer instanceof L.TileLayer.WMS) mapInstance.removeLayer(layer);
            });
            setMapReady(true);
            return;
        }

        const map = L.map(mapContainerRef.current, {
            center: position,
            zoom: 15,
            attributionControl: false,
            zoomControl: false,
        });

        L.control.zoom({ position: 'topright' }).addTo(map);

        const mapboxLayer = L.tileLayer(
            `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`,
            { tileSize: 512, zoomOffset: -1, maxZoom: 22, attribution: '' }
        ).addTo(map);
        mapboxBaseLayerRef.current = mapboxLayer;

        mapRef.current = map;
        mapInstance = map;
        setMapReady(true);

        return () => {};
    }, []);

    useEffect(() => {
        const checkbox = document.getElementById('collapse-toggle');
        if (!checkbox) return;
        const handleToggle = () => {
            setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 450);
        };
        checkbox.addEventListener('change', handleToggle);
        return () => checkbox.removeEventListener('change', handleToggle);
    }, []);





{/* Loader overlay — shown while a higher-quality composite loads on
    zoom-in/out. Placed inside map-section so it sits above the map but
    doesn't block the draw toolbar or panels (pointerEvents none). */}





    return (
        <div className="app-container">
            <Topbar onLocationSelect={handleLocationSearch} />
            <div className="main-wrapper">
                <SideMenu />

                <div className="map-section">
                    <div ref={mapContainerRef} style={{ height: "100%", width: "100%" }} />

                    {mapReady && (
                        <>
                            <LeafletDrawControls
                                onAreaChange={setTotalArea}
                                setGeometry={setGeometry}
                                initialGeometry={geometry}
                                map={mapRef.current}
                                onParcelsChange={setParcels}
                                autoBoundary={autoBoundary}
                                onBoundaryEdited={handleBoundaryEdited}
                                editRequest={editRequestToken}
                                clearRequest={clearRequestToken}
                                key={mapRef.current ? 'draw-controls' : 'no-map'}
                            />

                            {isNearmapLoading && (
    <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 999, pointerEvents: 'none',
    }}>
        <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '12px',
            padding: '14px 22px', display: 'flex', alignItems: 'center', gap: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)', fontFamily: 'sans-serif',
        }}>
            <div style={{
                width: '18px', height: '18px', borderRadius: '50%',
                border: '3px solid #e2e2e2', borderTopColor: '#00ff00',
                animation: 'nearmap-spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
                Loading sharper imagery…
            </span>
        </div>
    </div>
)}




                            {pendingLocation && (
                                <AddressConfirmPanel
                                    locationName={pendingLocation.name}
                                    onConfirm={handleConfirmAddress}
                                    onCancel={handleCancelAddress}
                                    isDetecting={isDetecting}
                                />
                            )}

                            {!pendingLocation && parcels.length > 0 && (
                                <ModifyParcelPanel
                                    parcels={parcels}
                                    totalAreaAcres={totalArea / 4046.8564224}
                                    confirmed={boundaryConfirmed}
                                    onConfirm={() => setBoundaryConfirmed(true)}
                                    imagerySource={imagerySource}
                                    onImageryChange={setImagerySource}
                                    surveys={nearmapSurveys}
                                    selectedSurveyId={selectedSurveyId}
                                    onSurveyChange={setSelectedSurveyId}
                                    onEditBoundary={handleEditBoundaryClick}
                                    onRemoveBoundary={handleRemoveBoundary}
                                />
                            )}
                        </>
                    )}
                </div>

                <div className="details-collapse-wrapper">
                    <input type="checkbox" id="collapse-toggle" className="collapse-checkbox" />
                    <label htmlFor="collapse-toggle" className="collapse-tab">
                        <span className="arrow-icon"></span>
                    </label>
                    <div className="details-content-container">
                        <ProjectDetails
    locationName={locationName}
    setLocationName={setLocationName}
    coords={position}
    area={totalArea}
    geometry={geometry}
    config={config}
    editData={editData}
    nearmapImage={nearmapImageData?.dataUrl || null}      // ← ADD
    nearmapVintage={nearmapImageData?.captureDate || null} // ← ADD
/>
                    </div>
                </div>
            </div>
            <style>
                {`
                .details-collapse-wrapper { display: flex; align-items: center; position: relative; height: 100%; z-index: 1000; }
                .details-content-container { width: 400px; height: 100%; transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); overflow: hidden; }
                .details-content-container .clean-premium-sidebar { width: 400px !important; max-width: none !important; height: 100%; }
                .collapse-tab { position: absolute; left: -20px; top: 50%; transform: translateY(-50%); width: 24px; height: 60px; background: #ffffff; border: 1px solid #e0e0e0; border-right: none; border-radius: 8px 0 0 8px; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: -4px 0 10px rgba(0,0,0,0.05); }
                .arrow-icon::before { content: ''; display: inline-block; border: solid #6a11cb; border-width: 0 3px 3px 0; padding: 3px; transform: rotate(-45deg); transition: transform 0.3s ease; }
                .collapse-checkbox { display: none; }
                .collapse-checkbox:checked ~ .details-content-container { width: 0; opacity: 0; }
                .collapse-checkbox:checked + .collapse-tab .arrow-icon::before { transform: rotate(135deg); }
                .collapse-checkbox:checked + .collapse-tab { left: -24px; }
                .map-section { transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1); }

   
.leaflet-editing-icon {
    width: 8px !important;
    height: 8px !important;
    margin-left: -4px !important;
    margin-top: -4px !important;
    background: #00ff00 !important;
    border: 2px solid #ffffff !important;
    border-radius: 50% !important;
    box-shadow: 0 1px 4px rgba(0,0,0,0.35) !important;
}

.leaflet-vertex-icon {
    width: 8px !important;
    height: 8px !important;
    margin-left: -4px !important;
    margin-top: -4px !important;
    background: #00ff00 !important;
    border: 2px solid #ffffff !important;
    border-radius: 50% !important;
    box-shadow: 0 1px 4px rgba(0,0,0,0.35) !important;
}

.leaflet-marker-icon.leaflet-div-icon {
    border-radius: 50% !important;
}

// Add this keyframe alongside the existing <style> block in Home's JSX
.map-section { transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1); }

@keyframes nearmap-spin {
    to { transform: rotate(360deg); }
}
                `}
            </style>
        </div>
    );
};

export default Home;